﻿import { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import GenericDialog from '../../../components/Dialog/GenericDialog';
import { Snackbar } from '../../../components/Global/Snackbar/Snackbar';
import { IconButton, Tooltip, Chip } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../../context/UserContext';
import {
  capitalizeFirstLetter,
  isFileLockOwner,
} from '../../../components/Utilities/HelperMethods';
//Icons
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import UploadIcon from '@mui/icons-material/Upload';
//API
import { AgencyRecordLock, GetUploadHistoryById } from '../../../api/SecureFileUploader';
//Components
import PdfDownloadWidget from '../../../components/Global/PdfDownloadWidget/PdfDownloadWidget';
import SecureFileUploaderRecordModal from './SecureFileUploaderRecordModal';

export default function SecureFileUploaderDataGrid({
  data,
  isSuccess,
  isLoading,
  permissions,
  refreshUploadedData,
}) {
  const queryClient = useQueryClient();
  const user = useUser();
  const { isAdmin } = permissions;

  const [columns, setColumns] = useState([]);
  const [rowData, setRowData] = useState([]);

  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [openPdfDownloadWidget, setOpenPdfDownloadWidget] = useState(false);
  const [isDownloadingAfterLock, setIsDownloadingAfterLock] = useState(false);
  const [snackbarDetails, setSnackbarDetails] = useState({
    isOpen: false,
    message: '',
    status: '',
    requiresAction: false,
  });

  const handleConfirmLock = async () => {
    if (selectedRecord == null) return;

    // If file is currently locked, just unlock it without downloading
    if (selectedRecord.fileLocked) {
      await agencyRecordLockMutation.mutateAsync(selectedRecord);
      setLockDialogOpen(false);
      setSelectedRecord(null);
    } else {
      // If unlocked, we need to lock it then download
      setIsDownloadingAfterLock(true);

      try {
        // First lock the file
        await agencyRecordLockMutation.mutateAsync(selectedRecord);

        // Then fetch the file content
        const fileData = await GetUploadHistoryById(selectedRecord.uploadID)();

        if (fileData?.data?.fileContent && fileData?.data?.uploadFileName) {
          // Trigger download
          const linkSource = `data:application/pdf;base64,${fileData.data.fileContent}`;
          const downloadLink = document.createElement('a');
          downloadLink.href = linkSource;
          downloadLink.download = fileData.data.uploadFileName;
          downloadLink.click();

          setSnackbarDetails({
            isOpen: true,
            message: 'File locked and downloaded successfully',
            status: 'success',
          });
        } else {
          throw new Error('File content not available');
        }

        // Close dialog and clean up
        setLockDialogOpen(false);
        setSelectedRecord(null);
      } catch (error) {
        setSnackbarDetails({
          isOpen: true,
          message: error?.message || 'An error occurred while downloading file',
          status: 'error',
        });
      } finally {
        setIsDownloadingAfterLock(false);
      }
    }
  };

  const handleLockDialogOpen = () => {
    setLockDialogOpen(true);
  };

  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
  };

  const handlePdfDownloadWidgetClose = () => {
    setSelectedRecord(null);
    setOpenPdfDownloadWidget(false);
  };

  const handlePdfDownloadWidgetOpen = () => {
    setOpenPdfDownloadWidget(true);
  };

  // helper fn to format boolean values in data grid
  const formatBooleanVal = (val, defaultVal = 'False') =>
    val !== null ? capitalizeFirstLetter(val.toString()) : defaultVal;

  const getUploadByID = useQuery({
    queryKey: ['GetUploadByID', selectedRecord != null ? selectedRecord.uploadID : -1],
    queryFn: GetUploadHistoryById(selectedRecord != null ? selectedRecord.uploadID : -1),
    enabled: !!(selectedRecord != null && openPdfDownloadWidget),
    onSuccess: (data) => {
      let showError = data?.error;

      setSnackbarDetails({
        isOpen: true,
        message: data?.message,
        status: showError != null ? 'error' : 'success',
      });
    },
    onError: (data) => {
      const errorMessage = data?.message || 'An error with uploaded file';

      setSnackbarDetails({
        isOpen: true,
        message: errorMessage,
        status: 'error',
      });
    },
  });

  const agencyRecordLockMutation = useMutation({
    mutationFn: (selectedRecord) =>
      AgencyRecordLock(selectedRecord.uploadID, selectedRecord.fileLocked ? false : true)(),
    onSuccess: (res) => {
      const message = res?.Message || res?.message || 'Lock/Unlock file successful';

      // Only show snackbar for unlock operations
      // Lock operations will show their own message after download completes
      if (selectedRecord?.fileLocked) {
        setSnackbarDetails({
          isOpen: true,
          message: message,
          status: 'success',
        });
      }

      queryClient.invalidateQueries(['GetFilesUploadedByAgencyAndStatus']);
      refreshUploadedData();
    },
    onError: (error) => {
      const errorMessage = error.message || 'An error occurred while locking/unlocking file';

      setSnackbarDetails({
        isOpen: true,
        message: errorMessage,
        status: 'error',
        requiresAction: true,
      });

      setLockDialogOpen(false);
      setIsDownloadingAfterLock(false);
    },
  });

  useEffect(() => {
    if (data) {
      var columnsArray = [
        {
          field: 'uploadDate',
          headerName: 'Date',
          valueFormatter: (value) => {
            const date = new Date(value);
            return date.toLocaleDateString();
          },
          flex: 1.5,
          align: 'left',
        },
        {
          field: 'uploadFileName',
          headerName: 'File Name',
          flex: 2,
          align: 'left',
        },
        {
          field: 'uploadStatus',
          headerName: 'Status',
          flex: 2,
          align: 'left',
          renderCell: (params) => {
            return (
              <Chip
                label={params.row.uploadStatus}
                variant="outlined"
                color={
                  params.row.uploadStatus === 'Success'
                    ? 'success'
                    : params.row.uploadStatus === 'Error'
                      ? 'error'
                      : 'default'
                }
              />
            );
          },
        },
        ...(isAdmin
          ? [
              {
                field: 'managerRequired',
                headerName: 'Manager Required',
                flex: 1.5,
                align: 'left',
                renderCell: (params) => {
                  return (
                    <Chip
                      variant="outlined"
                      color={params.row.managerRequired ? 'error' : 'default'}
                      label={formatBooleanVal(params.row.managerRequired)}
                    />
                  );
                },
              },
              {
                field: 'rsaRequired',
                headerName: 'RSA Required',
                flex: 1.5,
                align: 'left',
                renderCell: (params) => {
                  return (
                    <Chip
                      variant="outlined"
                      color={params.row.rsaRequired ? 'error' : 'default'}
                      label={formatBooleanVal(params.row.rsaRequired)}
                    />
                  );
                },
              },
              {
                field: 'fileLocked',
                headerName: 'Locked',
                flex: 1.5,
                align: 'left',
                renderCell: (params) => {
                  return (
                    <Chip
                      variant="outlined"
                      color={params.row.fileLocked ? 'error' : 'default'}
                      label={formatBooleanVal(params.row.fileLocked)}
                    />
                  );
                },
              },
              {
                field: 'fileLockedBy',
                headerName: 'Locked By',
                flex: 1.5,
                align: 'left',
              },
              {
                field: 'uploadedBy',
                headerName: 'Uploaded By',
                flex: 1.5,
                align: 'left',
              },
            ]
          : []),
        {
          field: 'actions',
          headerName: isAdmin ? 'Actions' : 'Download',
          flex: 2.3,
          align: 'center',
          sortable: false,
          renderCell: (params) => {
            const isLockOwner = isFileLockOwner(params.row, user);
            const shouldUpload = params.row.fileLocked && isLockOwner;
            return (
              <>
                {isAdmin && (
                  <Tooltip title={shouldUpload ? 'Upload file' : 'Lock/Unlock File'}>
                    <IconButton
                      variant="outlined"
                      disabled={params.row.uploadStatus === 'Success'}
                      size="small"
                      sx={{ mr: 1 }}
                      color={params.row.fileLocked ? 'info' : 'error'}
                      onClick={() => {
                        setSelectedRecord(params.row);
                        if (shouldUpload) {
                          handleUploadDialogOpen();
                        } else {
                          handleLockDialogOpen();
                        }
                      }}
                    >
                      {params.row.fileLocked ? (
                        isLockOwner ? (
                          <UploadIcon />
                        ) : (
                          <LockOutlinedIcon />
                        )
                      ) : (
                        <LockOpenOutlinedIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Download File">
                  <IconButton
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setSelectedRecord(params.row);
                      handlePdfDownloadWidgetOpen();
                    }}
                  >
                    <FileDownloadOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </>
            );
          },
        },
      ];

      setColumns(columnsArray);

      var rData = data?.data?.map((r, index) => ({
        id: `row-${index}`,
        uploadID: r.uploadID,
        uploadedBy: r.uploadedBy,
        uploadDate: r.uploadDate,
        uploadFileName: r.uploadFileName,
        uploadStatus: r.uploadStatus,
        managerRequired: r.managerRequired,
        rsaRequired: r.rsaRequired,
        rsaApproved: r.rsaApproved,
        fileLocked: r.fileLocked,
        fileLockedBy: r.fileLockedBy,
        rowIndex: index,
      }));

      setRowData(rData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data]);

  return (
    <div style={{ height: 500, width: '100%' }}>
      <GenericDialog
        open={lockDialogOpen}
        onClose={() => {
          if (!isDownloadingAfterLock) {
            setLockDialogOpen(false);
            setSelectedRecord(null);
          }
        }}
        onConfirm={handleConfirmLock}
        title={!selectedRecord?.fileLocked ? 'Lock File' : 'Unlock File'}
        cancelText="Cancel"
        confirmText={'Confirm'}
        confirmLoading={agencyRecordLockMutation.isLoading || getUploadByID.isLoading}
        confirmColor="primary"
        disableConfirm={isDownloadingAfterLock}
        content={
          !selectedRecord?.fileLocked
            ? 'Are you sure you want to lock this file? The file will be downloaded after locking.'
            : isFileLockOwner(selectedRecord, user)
              ? 'Are you sure you want to unlock this file?'
              : `Are you sure you want to override the lock on this file by: ${selectedRecord?.fileLockedBy}?`
        }
      />
      <SecureFileUploaderRecordModal
        isModalOpen={uploadDialogOpen}
        toggleModal={setUploadDialogOpen}
        recordID={selectedRecord?.uploadID}
      />
      {getUploadByID.isSuccess && getUploadByID.data && (
        <PdfDownloadWidget
          open={openPdfDownloadWidget}
          onClose={handlePdfDownloadWidgetClose}
          fileData={getUploadByID.data?.data?.fileContent}
          fileName={getUploadByID.data?.data?.uploadFileName}
        />
      )}
      <DataGrid
        sx={{
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'transparent',
          },
          '& .MuiDataGrid-row.Mui-hovered': {
            backgroundColor: 'transparent',
          },
          '& p': {
            margin: 0,
          },
          '& .MuiTablePagination-actions': {
            marginTop: 0,
          },
        }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
        rows={rowData}
        columns={columns}
        loading={isLoading}
        slotProps={{
          loadingOverlay: {
            variant: 'linear-progress',
          },
        }}
      />
      <Snackbar
        open={snackbarDetails.isOpen}
        message={snackbarDetails.message}
        status={snackbarDetails.status}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={() =>
          setSnackbarDetails((prev) => ({
            ...prev,
            isOpen: false,
          }))
        }
      />
    </div>
  );
}
